import dayjs from "dayjs";
import { DocumentReference } from "fhir/r5";
import { GenericTable } from "../../../../../../components/GenericTable/GenericTable";
import { ProcedureRequestActions } from "../../ProcedureRequest/ProcedureRequestActions";
import { DocumentCard } from "../../../../../../components/common/DocumentCard";
import { Stack } from "@mui/material";
import { isMobile } from "react-device-detect";
import { MedicalServices } from "@mui/icons-material";
import { NoDataWarning } from "@/components/common";

// Função auxiliar para buscar informações do procedimento no documento
function extractProcedureInfo(doc: any): string {
  // Tenta obter do campo code que é específico para o tipo de procedimento
  if (doc?.resource?.code?.coding?.[0]?.display) {
    return doc?.resource?.code?.coding?.[0]?.display;
  }
  
  // Tenta obter de subject.display que pode conter o tipo de procedimento
  if (doc?.resource?.subject?.display) {
    return doc?.resource?.subject?.display;
  }
  
  // Tenta obter do basedOn.display
  if (doc?.resource?.basedOn?.[0]?.display) {
    return doc?.resource?.basedOn[0]?.display;
  }
  
  // Tenta obter do título do documento
  if (doc?.resource?.title) {
    return doc?.resource?.title;
  }
  
  // Tenta obter do description
  if (doc?.resource?.description) {
    return doc?.resource?.description;
  }
  
  // Tenta obter do context.event
  if (doc?.resource?.context?.event?.[0]?.coding?.[0]?.display) {
    return doc?.resource?.context?.event?.[0]?.coding?.[0]?.display;
  }
  
  // Tenta obter do type
  if (doc?.resource?.type?.coding?.[0]?.display) {
    return doc?.resource?.type?.coding?.[0]?.display;
  }
  
  // Tenta obter de extension
  if (doc?.resource?.extension?.[0]?.valueCoding?.display) {
    return doc?.resource?.extension[0].valueCoding.display;
  }
  
  // Valor padrão
  return "";
}

/**
 * Componente para exibir procedimentos médicos do paciente
 * Implementa uma aba dentro do PatientDocs seguindo o mesmo padrão do ProcedureRequest
 */
export function Procedures({
  data,
  resultsData,
  handleOpen,
  setBinaryUrl,
  sign,
  setRefetch,
  procedureServiceRequests,
}: any) {
  // Pega os resultados associados
  const results = resultsData?.[0]?.entry;

  // Filtra os DocumentReference relacionados a procedimentos
  // Verifica se tem documento disponível para download
  const procedureDocuments = data?.filter((el: any) => {
    // Verifica se o documento tem URL para download
    return !!el?.resource?.content?.[0]?.attachment?.url;
  });

  // Define as colunas da tabela
  const columns = [
    {
      column: "Descrição",
      rowName: "description",
    },
    { column: "Data", rowName: "date" },
    { column: "Profissional solicitante", rowName: "practitioner" },
  ];

  // Mapeia os dados para a tabela - seguindo exatamente o mesmo padrão do ProcedureRequest.tsx
  const rows = procedureDocuments
    ?.map((doc: any, index: number) => {
      // Obtém informações do documento
      const binaryUrl = doc?.resource?.content?.[0]?.attachment?.url || "";
      
      // Obter o ServiceRequest associado
      const serviceRequestId = doc?.resource?.basedOn?.[0]?.reference?.split('/')[1];
      let relatedServiceRequest = null;
      
      if (serviceRequestId && procedureServiceRequests?.[0]?.entry) {
        relatedServiceRequest = procedureServiceRequests[0].entry.find((sr: any) => 
          sr?.resource?.id === serviceRequestId
        );
      }
      
      // Obter descrição do procedimento - EXATAMENTE como é feito no ProcedureRequest.tsx
      let description = "";
      
      if (relatedServiceRequest?.resource?.code?.concept?.coding?.[0]?.display) {
        // Pega exatamente do mesmo lugar que o ProcedureRequest.tsx
        description = relatedServiceRequest.resource.code.concept.coding[0].display;
      } else if (doc?.resource?.type?.coding?.[0]?.display) {
        // Se não encontrar no ServiceRequest, pega do tipo do documento
        description = doc.resource.type.coding[0].display;
      } else if (doc?.resource?.title) {
        // Última tentativa: pegar do título do documento
        description = doc.resource.title;
      } else {
        description = "Procedimento";
      }
      
      // Pega o profissional exatamente da mesma forma que o ProcedureRequest
      let practitionerName = "-";
      
      // Primeiro tenta do documento
      if (doc?.resource?.contained) {
        const practitioner = doc.resource.contained.find(
          (cont: any) => cont.resourceType === "Practitioner"
        );
        
        if (practitioner?.name?.[0]?.text) {
          practitionerName = practitioner.name[0].text;
        }
      }
      
      // Se não encontrou, tenta do ServiceRequest
      if (practitionerName === "-" && relatedServiceRequest?.resource?.contained?.[0]?.name?.[0]?.text) {
        practitionerName = relatedServiceRequest.resource.contained[0].name[0].text;
      }
      
      // Formata a data como o ProcedureRequest
      const date = relatedServiceRequest?.resource?.authoredOn 
        ? dayjs(relatedServiceRequest.resource.authoredOn).format("DD/MM/YYYY") 
        : dayjs(doc?.resource?.date).format("DD/MM/YYYY") || "-";
      
      // Obtém o ID do encontro (se disponível)
      const encounter = doc?.resource?.context?.encounter?.reference;
      let encounterId = encounter ? encounter.split("/")?.[1] : undefined;
      
      // Se não houver ServiceRequest, tentar obter ID do encontro do documento
      if (!encounterId && relatedServiceRequest?.resource?.encounter?.reference) {
        const encounterRef = relatedServiceRequest.resource.encounter.reference;
        encounterId = encounterRef.split("/")?.[1];
      }
      
      return {
        id: index,
        description,
        date,
        practitioner: practitionerName,
        binaryUrl,
        encounterId,
        fullResource: doc?.resource,
        fullServiceResource: relatedServiceRequest ? [relatedServiceRequest] : [],
        status: relatedServiceRequest?.resource?.status || "active"
      };
    })
    ?.filter((row: any) => row.binaryUrl) // Mostra apenas se tiver documento disponível
    ?.sort((a: any, b: any) => {
      // Ordena por data decrescente
      const dateA = dayjs(a.date, "DD/MM/YYYY");
      const dateB = dayjs(b.date, "DD/MM/YYYY");
      return dateB.diff(dateA);
    }) || [];

  // Renderização para dispositivos móveis
  if (isMobile) {
    return (
      <Stack spacing={2}>
        {rows.length > 0 ? (
          rows.map((row: any) => (
            <DocumentCard
              key={row.id}
              title={row.description || "Procedimento"}
              date={row.date}
              practitioner={row.practitioner}
              status={{
                text: "Procedimento",
                color: "#9C27B0",
                icon: <MedicalServices fontSize="small" />,
              }}
              actions={
                <ProcedureRequestActions
                  row={row}
                  handleOpen={handleOpen}
                  setBinaryUrl={setBinaryUrl}
                />
              }
            />
          ))
        ) : (
          <NoDataWarning />
        )}
      </Stack>
    );
  }

  // Renderização para desktop
  return (
    <GenericTable
      tableColumns={columns}
      tableRows={rows}
      actions={(rowData: any) => (
        <ProcedureRequestActions
          row={rowData}
          handleOpen={handleOpen}
          setBinaryUrl={setBinaryUrl}
        />
      )}
    />
  );
}
